import React, { useRef, useEffect } from "react";

export interface VaultDocItem {
  id: string;
  category: string;
  title: string;
  date: string;
  status: "action" | "accepted" | "read" | "pending";
}

const STATUS_DISPLAY: Record<string, { label: string; className: string }> = {
  action: { label: "[ ! ] Action Required", className: "vault-status-action" },
  accepted: { label: "[ \u2713 ] Accepted", className: "vault-status-accepted" },
  read: { label: "[ \u2022 ] Read", className: "vault-status-read" },
  pending: { label: "[ \u2022 ] Pending", className: "vault-status-read" },
};

interface VaultSidebarProps {
  documents: VaultDocItem[];
  activeId?: string;
  onSelect: (id: string) => void;
  loading?: boolean;
}

export const VaultSidebar: React.FC<VaultSidebarProps> = ({
  documents,
  activeId,
  onSelect,
  loading = false,
}) => {
  const activeRef = useRef<HTMLDivElement>(null);

  // Scroll the active item into view when activeId changes
  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [activeId]);

  return (
    <aside className="vault-panel vault-sidebar">
      <div className="vault-sidebar-header">
        <span className="vault-label-tiny">Documents</span>
        <span className="vault-label-tiny">
          [ {String(documents.length).padStart(2, "0")} ]
        </span>
      </div>

      <div className="vault-document-list">
        {loading && (
          <div className="vault-loading" style={{ padding: "2rem" }}>
            Loading...
          </div>
        )}

        {!loading && documents.length === 0 && (
          <div className="vault-empty" style={{ padding: "2rem" }}>
            No documents
          </div>
        )}

        {documents.map((doc) => {
          const isActive = doc.id === activeId;
          const statusConfig = STATUS_DISPLAY[doc.status] || STATUS_DISPLAY.pending;
          return (
            <div
              key={doc.id}
              ref={isActive ? activeRef : undefined}
              className={`vault-doc-item ${isActive ? "active" : ""}`}
              onClick={() => onSelect(doc.id)}
            >
              <div className="vault-doc-meta-row">
                <span className="vault-label-bracketed">{doc.category}</span>
                <span className={`vault-doc-status ${statusConfig.className}`}>
                  {statusConfig.label}
                </span>
              </div>
              <div className="vault-doc-title">{doc.title}</div>
              <div className="vault-doc-date">Issued: {doc.date}</div>
            </div>
          );
        })}
      </div>
    </aside>
  );
};
